import type { ReactNode } from 'react';

import { useCarousel } from '~/hooks/use-carousel';
import { m } from '~/paraglide/messages.js';

interface LoginLayoutProps {
  children: ReactNode;
}

const getCarouselData = () => [
  {
    image: '/login-slider1.webp',
    title: m['login_layout.carousel.title_1'](),
    subtitle: m['login_layout.subtitle'](),
  },
  {
    image: '/login-slider2.webp',
    title: m['login_layout.carousel.title_2'](),
    subtitle: m['login_layout.subtitle'](),
  },
  {
    image: '/login-slider3.webp',
    title: m['login_layout.carousel.title_3'](),
    subtitle: m['login_layout.subtitle'](),
  },
];

export function LoginLayout({ children }: LoginLayoutProps) {
  const carouselData = getCarouselData();
  const { currentIndex, currentData, goToSlide, pause, resume } =
    useCarousel(carouselData);

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero carousel */}
      <section
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        onMouseEnter={pause}
        onMouseLeave={resume}
        aria-label="Hero carousel"
      >
        {/* Carousel images */}
        <div
          className="flex transition-transform duration-500 ease-in-out w-full h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {carouselData.map((slide, index) => (
            <div key={slide.image} className="min-w-full h-full relative">
              <img
                src={slide.image}
                alt={`Slide ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
              {/* Bottom gradient overlay only */}
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            </div>
          ))}
        </div>

        {/* Text overlay */}
        <div className="absolute bottom-8 left-8 text-white">
          <h2 className="text-2xl font-semibold mb-2 transition-opacity duration-300">
            {currentData.title}
          </h2>
          <p className="text-sm opacity-90 transition-opacity duration-300">
            {currentData.subtitle}
          </p>
        </div>

        {/* Navigation dots */}
        <div className="absolute bottom-8 right-8 flex space-x-2">
          {carouselData.map((_, index) => (
            <button
              key={`dots-${index}`}
              type="button"
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white'
                  : 'bg-white/60 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      <div className="w-full lg:w-1/2 flex flex-col p-8">{children}</div>
    </div>
  );
}
